import React, { ReactNode, forwardRef } from "react";
import { Box, BoxProps } from "../Box/Box";
import { Corner, CornerProps } from "../Corner/Corner";
import { Flex } from "../Flex/Flex";

export interface CardProps extends Omit<BoxProps, "css"> {
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  corner?: CornerProps | boolean;
  direction?: "row" | "column";
  variant?: "ghost" | "solid" | "outline";
}

type ICardVariantsConfig = {
  [key in CardProps["variant"]]: Omit<BoxProps, "css">;
};

const themeVariants: ICardVariantsConfig = {
  ghost: {
    background: "transparent",
  },
  solid: {
    background: "white",
    shadow: "md",
  },
  outline: {
    background: "transparent",
    borderColor: "gray.300",
    borderWidth: "1px",
  },
};

// eslint-disable-next-line react/display-name
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      header,
      footer,
      corner,
      direction = "column",
      variant = "solid",
      ...props
    }: CardProps,
    ref
  ) => {
    const cornerProps = corner === true ? {} : corner;

    const containerProps = {
      ...themeVariants[variant],
      ...props,
    };

    return (
      <Flex
        ref={ref}
        rounded="lg"
        padding={[6, 8]}
        flexDirection={direction}
        height="100%"
        overflow="hidden"
        position="relative"
        _hover={{ color: "inherit" }}
        {...containerProps}
      >
        {corner && <Corner {...cornerProps} />}
        {header && header}
        {children && (
          <Box flexGrow={1} alignSelf="flex-start" width="100%">
            {children}
          </Box>
        )}
        {footer && (
          <Flex alignItems="center" marginTop="auto">
            {footer}
          </Flex>
        )}
      </Flex>
    );
  }
);
